import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CurrencyFormat from 'react-currency-format';
import AuthService from '../../../AuthService';
import { FiCheck, FiEdit2, FiRefreshCw, FiSearch, FiTrash2 } from 'react-icons/fi';
import { Loader } from '../../Login/style';
import {
    ActionButton,
    ActionGroup,
    DesktopShell,
    EmptyState,
    EnterpriseTable,
    FilterInput,
    HeaderCard,
    HeaderTop,
    InlineLabel,
    LoadingCard,
    LoadingOverlay,
    PageContainer,
    ProductHint,
    ProductImage,
    ProductMeta,
    ProductName,
    ProductWrap,
    QueryPill,
    RangeInput,
    SecondaryButton,
    SortSelect,
    StatCard,
    StatLabel,
    StatValue,
    StatsGrid,
    StockEditWrap,
    StockInput,
    StockPill,
    Subtitle,
    TableBody,
    TableCard,
    TableCell,
    TableHead,
    TableHeadCell,
    TableRow,
    TableScroller,
    Title,
    Toolbar,
    ToolbarActions,
    ToolbarFilters,
} from './search.style';

export default function SearchResults() {
    const location = useLocation();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [totalResults, setTotalResults] = useState(0);
    const [loading, setLoading] = useState(false);
    const [stockDrafts, setStockDrafts] = useState({});
    const [updatingStock, setUpdatingStock] = useState({});
    const [deletingProduct, setDeletingProduct] = useState({});
    const [localFilter, setLocalFilter] = useState('');
    const [referenceFrom, setReferenceFrom] = useState('');
    const [referenceTo, setReferenceTo] = useState('');
    const [sortBy, setSortBy] = useState('reference');
    const [sortDirection, setSortDirection] = useState('desc');

    const searchTerm = useMemo(() => {
        return (new URLSearchParams(location.search).get('s') || '').trim();
    }, [location.search]);

    const fetchSearchResults = useCallback(async (term) => {
        setLoading(true);
        try {
            const response = await fetch(`https://api.rb.beeapp.es/admin/search?s=${encodeURIComponent(term)}&allProducts=true&includeUsers=false`, {
                headers: {
                    Authorization: `Bearer ${AuthService.getToken()}`,
                },
            });

            if (!response.ok) {
                throw new Error('No se pudieron cargar los resultados');
            }

            const data = await response.json();
            const foundProducts = data?.products?.productsSearch || [];
            const total = data?.products?.totalSearchedProducts || foundProducts.length;

            setProducts(foundProducts);
            setTotalResults(total);
            setLocalFilter('');
            setReferenceFrom('');
            setReferenceTo('');
            setStockDrafts(
                foundProducts.reduce((acc, product) => {
                    acc[product._id] = String(product.stock ?? 0);
                    return acc;
                }, {})
            );
        } catch (error) {
            console.error('Error searching admin products:', error);
            toast.error('Error al buscar productos');
            setProducts([]);
            setTotalResults(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!searchTerm) {
            setProducts([]);
            setTotalResults(0);
            setStockDrafts({});
            return;
        }
        fetchSearchResults(searchTerm);
    }, [searchTerm, fetchSearchResults]);

    const handleStockInputChange = (productId, value) => {
        if (!/^\d*$/.test(value)) return;
        setStockDrafts((prev) => ({
            ...prev,
            [productId]: value,
        }));
    };

    const updateProductStock = async (productId) => {
        const parsedStock = Number.parseInt(stockDrafts[productId], 10);
        if (Number.isNaN(parsedStock) || parsedStock < 0) {
            toast.error('El stock debe ser un número válido');
            return;
        }

        setUpdatingStock((prev) => ({ ...prev, [productId]: true }));
        try {
            const response = await fetch(`https://api.rb.beeapp.es/admin/product/minimal/${productId}`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${AuthService.getToken()}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ stock: parsedStock }),
            });

            if (!response.ok) {
                throw new Error('No se pudo actualizar el stock');
            }

            setProducts((prev) =>
                prev.map((product) =>
                    product._id === productId
                        ? { ...product, stock: parsedStock }
                        : product
                )
            );
            setStockDrafts((prev) => ({
                ...prev,
                [productId]: String(parsedStock),
            }));
            toast.success('Stock actualizado');
        } catch (error) {
            console.error('Error updating stock:', error);
            toast.error('Error al actualizar el stock');
        } finally {
            setUpdatingStock((prev) => ({ ...prev, [productId]: false }));
        }
    };

    const deleteProduct = async (productId) => {
        const confirmed = window.confirm('¿Estás seguro de que quieres eliminar este producto?');
        if (!confirmed) return;

        setDeletingProduct((prev) => ({ ...prev, [productId]: true }));
        try {
            const response = await fetch(`https://api.rb.beeapp.es/admin/product/${productId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${AuthService.getToken()}`,
                },
            });

            if (!response.ok) {
                throw new Error('No se pudo eliminar el producto');
            }

            setProducts((prev) => prev.filter((product) => product._id !== productId));
            setStockDrafts((prev) => {
                const next = { ...prev };
                delete next[productId];
                return next;
            });
            setTotalResults((prev) => Math.max(0, prev - 1));
            toast.success('Producto eliminado');
        } catch (error) {
            console.error('Error deleting product:', error);
            toast.error('Error al eliminar el producto');
        } finally {
            setDeletingProduct((prev) => ({ ...prev, [productId]: false }));
        }
    };

    const sanitizeReference = (value) => {
        const digits = String(value || '').replace(/\D/g, '');
        if (!digits) return null;
        const parsed = Number.parseInt(digits, 10);
        return Number.isNaN(parsed) ? null : parsed;
    };

    const visibleProducts = useMemo(() => {
        const term = localFilter.trim().toLowerCase();
        const refFromNumber = sanitizeReference(referenceFrom);
        const refToNumber = sanitizeReference(referenceTo);
        const hasReferenceRange = referenceFrom.trim() !== '' || referenceTo.trim() !== '';

        const filtered = products.filter((product) => {
            const name = String(product.name || '').toLowerCase();
            const reference = String(product.reference || '').toLowerCase();
            const ean = String(product.EAN || '').toLowerCase();
            const textMatches = term ? (name.includes(term) || reference.includes(term) || ean.includes(term)) : true;
            if (!textMatches) return false;

            if (!hasReferenceRange) return true;

            const productReferenceNumber = sanitizeReference(product.reference);
            if (productReferenceNumber === null) return false;
            if (refFromNumber !== null && productReferenceNumber < refFromNumber) return false;
            if (refToNumber !== null && productReferenceNumber > refToNumber) return false;
            return true;
        });

        const sorted = [...filtered].sort((a, b) => {
            const direction = sortDirection === 'asc' ? 1 : -1;

            if (sortBy === 'name') {
                return String(a.name || '').localeCompare(String(b.name || '')) * direction;
            }
            if (sortBy === 'stock') {
                return ((a.stock || 0) - (b.stock || 0)) * direction;
            }
            if (sortBy === 'price') {
                return ((a.price || 0) - (b.price || 0)) * direction;
            }

            const refA = sanitizeReference(a.reference);
            const refB = sanitizeReference(b.reference);
            if (refA !== null && refB !== null) {
                return (refA - refB) * direction;
            }
            return String(a.reference || '').localeCompare(String(b.reference || '')) * direction;
        });

        return sorted;
    }, [products, localFilter, referenceFrom, referenceTo, sortBy, sortDirection]);

    const stockStats = useMemo(() => {
        const inStock = products.filter((product) => (product.stock || 0) > 0).length;
        const lowStock = products.filter((product) => (product.stock || 0) > 0 && (product.stock || 0) < 10).length;
        const noStock = products.filter((product) => (product.stock || 0) <= 0).length;
        return { inStock, lowStock, noStock };
    }, [products]);

    const getStockTone = (stock) => {
        if ((stock || 0) <= 0) return 'danger';
        if ((stock || 0) < 10) return 'warn';
        return 'success';
    };

    return (
        <>
            {loading && (
                <LoadingOverlay>
                    <LoadingCard>
                        <Loader />
                    </LoadingCard>
                </LoadingOverlay>
            )}

            <PageContainer>
                <DesktopShell>
                    <HeaderCard>
                        <HeaderTop>
                            <Title>Resultados de busqueda</Title>
                            {searchTerm && <QueryPill>Busqueda: {searchTerm}</QueryPill>}
                        </HeaderTop>
                        <Subtitle>
                            {searchTerm
                                ? `${totalResults} productos encontrados para la busqueda`
                                : 'Escribe una busqueda en el buscador del admin y pulsa Enter'}
                        </Subtitle>

                        <StatsGrid>
                            <StatCard>
                                <StatLabel>Total resultados</StatLabel>
                                <StatValue>{totalResults}</StatValue>
                            </StatCard>
                            <StatCard tone="success">
                                <StatLabel>Con stock</StatLabel>
                                <StatValue>{stockStats.inStock}</StatValue>
                            </StatCard>
                            <StatCard tone="danger">
                                <StatLabel>Sin stock</StatLabel>
                                <StatValue>{stockStats.noStock}</StatValue>
                            </StatCard>
                            <StatCard>
                                <StatLabel>Stock bajo (&lt;10)</StatLabel>
                                <StatValue>{stockStats.lowStock}</StatValue>
                            </StatCard>
                        </StatsGrid>

                        <Toolbar>
                            <ToolbarFilters>
                                <FilterInput
                                    value={localFilter}
                                    placeholder="Filtrar en resultados por nombre, referencia o EAN..."
                                    onChange={(event) => setLocalFilter(event.target.value)}
                                />
                                <InlineLabel>Referencia</InlineLabel>
                                <RangeInput
                                    value={referenceFrom}
                                    placeholder="Desde"
                                    onChange={(event) => setReferenceFrom(event.target.value)}
                                />
                                <InlineLabel>-</InlineLabel>
                                <RangeInput
                                    value={referenceTo}
                                    placeholder="Hasta"
                                    onChange={(event) => setReferenceTo(event.target.value)}
                                />
                                <InlineLabel>Ordenar por</InlineLabel>
                                <SortSelect
                                    value={sortBy}
                                    onChange={(event) => setSortBy(event.target.value)}
                                >
                                    <option value="reference">Referencia</option>
                                    <option value="name">Nombre</option>
                                    <option value="stock">Stock</option>
                                    <option value="price">Precio</option>
                                </SortSelect>
                                <SortSelect
                                    value={sortDirection}
                                    onChange={(event) => setSortDirection(event.target.value)}
                                >
                                    <option value="desc">Mayor a menor</option>
                                    <option value="asc">Menor a mayor</option>
                                </SortSelect>
                            </ToolbarFilters>
                            <ToolbarActions>
                                <SecondaryButton
                                    onClick={() => {
                                        if (searchTerm) {
                                            fetchSearchResults(searchTerm);
                                        }
                                    }}
                                    disabled={loading || !searchTerm}
                                >
                                    <FiRefreshCw size={12} />
                                    Recargar
                                </SecondaryButton>
                                <SecondaryButton
                                    onClick={() => {
                                        setLocalFilter('');
                                        setReferenceFrom('');
                                        setReferenceTo('');
                                        setSortBy('reference');
                                        setSortDirection('desc');
                                    }}
                                    disabled={!localFilter && !referenceFrom && !referenceTo && sortBy === 'reference' && sortDirection === 'desc'}
                                >
                                    <FiSearch size={12} />
                                    Limpiar filtros
                                </SecondaryButton>
                            </ToolbarActions>
                        </Toolbar>
                    </HeaderCard>

                    {visibleProducts.length > 0 ? (
                        <TableCard>
                            <TableScroller>
                                <EnterpriseTable>
                                    <TableHead>
                                        <tr>
                                            <TableHeadCell>Producto</TableHeadCell>
                                            <TableHeadCell>Referencia</TableHeadCell>
                                            <TableHeadCell>EAN</TableHeadCell>
                                            <TableHeadCell>Stock</TableHeadCell>
                                            <TableHeadCell>Actualizar stock</TableHeadCell>
                                            <TableHeadCell>Precio</TableHeadCell>
                                            <TableHeadCell>Acciones</TableHeadCell>
                                        </tr>
                                    </TableHead>
                                    <TableBody>
                                        {visibleProducts.map((product) => {
                                            const currentStock = product.stock || 0;
                                            const draftValue = stockDrafts[product._id] ?? '';
                                            const isSaving = Boolean(updatingStock[product._id]);
                                            const isDeleting = Boolean(deletingProduct[product._id]);
                                            const isDirty = String(currentStock) !== String(draftValue);
                                            return (
                                                <TableRow key={product._id}>
                                                    <TableCell>
                                                        <ProductWrap>
                                                            <ProductImage
                                                                src={product.images?.[0] || 'https://cdn.beeapp.es/distribucionesrb/NoImage.png'}
                                                                alt={product.name}
                                                                onError={(event) => {
                                                                    event.currentTarget.src = 'https://cdn.beeapp.es/distribucionesrb/NoImage.png';
                                                                }}
                                                            />
                                                            <ProductMeta>
                                                                <ProductName title={product.name}>{product.name}</ProductName>
                                                                <ProductHint>ID: {product._id}</ProductHint>
                                                            </ProductMeta>
                                                        </ProductWrap>
                                                    </TableCell>
                                                    <TableCell>{product.reference || '-'}</TableCell>
                                                    <TableCell>{product.EAN || '-'}</TableCell>
                                                    <TableCell>
                                                        <StockPill tone={getStockTone(currentStock)}>
                                                            {currentStock}
                                                        </StockPill>
                                                    </TableCell>
                                                    <TableCell>
                                                        <StockEditWrap>
                                                            <StockInput
                                                                value={draftValue}
                                                                onChange={(event) => handleStockInputChange(product._id, event.target.value)}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        event.preventDefault();
                                                                        updateProductStock(product._id);
                                                                    }
                                                                }}
                                                            />
                                                            <ActionButton
                                                                tone="success"
                                                                disabled={isSaving || isDeleting || !isDirty}
                                                                onClick={() => updateProductStock(product._id)}
                                                            >
                                                                <FiCheck size={12} />
                                                                Guardar
                                                            </ActionButton>
                                                        </StockEditWrap>
                                                    </TableCell>
                                                    <TableCell>
                                                        <CurrencyFormat
                                                            value={product.price}
                                                            displayType={'text'}
                                                            thousandSeparator={false}
                                                            suffix={'€'}
                                                            decimalSeparator=','
                                                            decimalScale={2}
                                                            fixedDecimalScale={true}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <ActionGroup>
                                                            <ActionButton
                                                                disabled={isSaving || isDeleting}
                                                                onClick={() => navigate(`/admin/product-edit/${product._id}`)}
                                                            >
                                                                <FiEdit2 size={12} />
                                                                Editar
                                                            </ActionButton>
                                                            <ActionButton
                                                                tone="danger"
                                                                disabled={isSaving || isDeleting}
                                                                onClick={() => deleteProduct(product._id)}
                                                            >
                                                                <FiTrash2 size={12} />
                                                                Eliminar
                                                            </ActionButton>
                                                        </ActionGroup>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </EnterpriseTable>
                            </TableScroller>
                        </TableCard>
                    ) : (
                        <EmptyState>
                            {searchTerm
                                ? 'No hay productos para mostrar con los filtros actuales.'
                                : 'No hay resultados para mostrar.'}
                        </EmptyState>
                    )}
                </DesktopShell>
            </PageContainer>
        </>
    );
}
